import { useEffect, useRef, useState, useCallback } from 'react';
import {
  FileText, Clock,
  Radio,
  // Phase 7g.4b.4 Commit 2 closed out the SP-placeholder icon set.
  // The /dashboard, /my-chargers, and /me placeholders all swapped
  // to real Sales Partner pages across Phase 7g.4b.2 → 7g.4b.4, so
  // LayoutDashboard / Plug / User all dropped from this import block.
  // FileText and Radio still serve the audit + RFID placeholder
  // routes elsewhere in the switch.
} from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useQuery } from '@tanstack/react-query';
import { fetchMe, fetchFleets } from '@/lib/api';
import { rememberReturnPath, takeReturnPath } from '@/lib/returnPath';
import { useBootstrapAuth } from '@/hooks/useBootstrapAuth';
import { BootSplash } from '@/components/BootSplash';
import { LoginPage } from '@/pages/login/LoginPage';
import { SalesPartnerInvitePage } from '@/pages/auth/SalesPartnerInvitePage';
import { MagicLinkPage } from '@/pages/auth/MagicLinkPage';
import { MagicLinkVerifyPage } from '@/pages/auth/MagicLinkVerifyPage';
import { BuildingInvitePage } from '@/pages/auth/BuildingInvitePage';
import { AlertPreferencesPage } from '@/pages/auth/AlertPreferencesPage';
import { BreakerDonePage } from '@/pages/auth/BreakerDonePage';
import { AlertSnoozePage } from '@/pages/auth/AlertSnoozePage';
import { SiteHostOnboardingPage } from '@/pages/auth/SiteHostOnboardingPage';
import { FacilitiesPage } from '@/pages/facilities/FacilitiesPage';
import { MfaEnrollPage } from '@/pages/auth/MfaEnrollPage';
import { MfaChallengePage } from '@/pages/auth/MfaChallengePage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { SalesPartnerDashboardPage } from '@/pages/sp-dashboard/SalesPartnerDashboardPage';
import { SalesBookPartnersPage, SalesBookOrdersPage, SalesBookOnboardingPage } from '@/pages/sales-team/SalesBookPages';
import { SalesStaffPage } from '@/pages/sales-team/SalesStaffPage';
import { SalesPayoutRunPage } from '@/pages/sales-team/SalesPayoutRunPage';
import { SalesTodayPage } from '@/pages/sales-team/SalesTodayPage';
import { TodayPage } from '@/pages/today/TodayPage';
import { ApprovalsPage } from '@/pages/approvals/ApprovalsPage';
import { TODAY_PATH, landsOnToday } from '@/lib/todayInbox';
import { SalesJobsPage } from '@/pages/sales-team/SalesJobsPage';
import { NewJobPage } from '@/pages/partner-jobs/NewJobPage';
import { JobDetailPage } from '@/pages/partner-jobs/JobDetailPage';
import { JobSitePage } from '@/pages/partner-jobs/JobSitePage';
import { WelcomePage } from '@/pages/partner-jobs/WelcomePage';
import { CustomersPage } from '@/pages/partner-customers/CustomersPage';
import { QuotesPage } from '@/pages/partner-quotes/QuotesPage';
import { QuoteBuilderPage } from '@/pages/partner-quotes/QuoteBuilderPage';
import { SurveysPage } from '@/pages/partner-surveys/SurveysPage';
import { SalesKitPage } from '@/pages/partner-quotes/SalesKitPage';
import { CustomerQuotePage } from '@/pages/partner-quotes/CustomerQuotePage';
import { CustomerPayPage, FreightPayPage } from '@/pages/partner-jobs/CustomerPayPage';
import { PortfolioPage } from '@/pages/partner-quotes/PortfolioPage';
import { SalesTeamViewPage } from '@/pages/sales-team/SalesTeamViewPage';
import { SalesCommissionPage,SalesStatementsPage } from '@/pages/sales-team/SalesCommissionPage';
import { SalesPartnerReportsPage, SalesPartnerCommissionsPage } from '@/pages/sp-reports/SalesPartnerReportsPage';
import { BuildingAdminReportsPage } from '@/pages/building-admin-reports/BuildingAdminReportsPage';
import { MySitePage } from '@/pages/site/MySitePage';
import { SiteEarningsPage } from '@/pages/site/SiteEarningsPage';
import { SiteSettingsPage } from '@/pages/site/SiteSettingsPage';
import { OwnerPayoutsPage } from '@/pages/site/OwnerPayoutsPage';
import { SalesPartnerMyChargersPage } from '@/pages/sp-my-chargers/SalesPartnerMyChargersPage';
import { SpRegisterInstallPage } from '@/pages/sp-installs/SpRegisterInstallPage';
import { SalesPartnerProfilePage } from '@/pages/sp-profile/SalesPartnerProfilePage';
import { SuperAdminProfilePage } from '@/pages/profile/SuperAdminProfilePage';
import { AppShell } from '@/components/ui/AppShell';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { LocationsPage } from '@/pages/locations/LocationsPage';
import { LocationDetailPage } from '@/pages/locations/LocationDetailPage';
import { ChargersPage } from '@/pages/chargers/ChargersPage';
import { ChargerDetailPage } from '@/pages/chargers/ChargerDetailPage';
import { MissingIccidPage } from '@/pages/chargers/MissingIccidPage';
import { CHARGER_OVERSIGHT_PATH, QUERY_CHANGE_EVENT, isQueryOnlyMove, oversightRedirectUrl } from '@/lib/chargerScope';
import { AnomaliesPage } from '@/pages/anomalies/AnomaliesPage';
import { AlertsPage } from '@/pages/alerts/AlertsPage';
import { AgentLogPage } from '@/pages/agent-log/AgentLogPage';
import { SimReconciliationPage } from '@/pages/sim-reconciliation/SimReconciliationPage';
import { TariffsPage } from '@/pages/tariffs/TariffsPage';
import { SessionsPage } from '@/pages/sessions/SessionsPage';
import { SessionDetailPage } from '@/pages/sessions/SessionDetailPage';
import { OrdersPage } from '@/pages/orders/OrdersPage';
import ProcurementPage from '@/pages/ProcurementPage';
import { EviQueuesPage } from '@/pages/evi-queues/EviQueuesPage';
import { ServiceRequestsPage } from '@/pages/service-requests/ServiceRequestsPage';
import { ServiceRequestDetailPage } from '@/pages/service-requests/ServiceRequestDetailPage';
import FactorySettingsPage from '@/pages/FactorySettingsPage';
import SupplierPoPage from '@/pages/SupplierPoPage';
import { OrderDetailPage } from '@/pages/orders/OrderDetailPage';
import { RevenuePage } from '@/pages/billing/RevenuePage';
import { MoneyPage } from '@/pages/money/MoneyPage';
import { PayoutsPage } from '@/pages/billing/PayoutsPage';
import { BadgesPage } from '@/pages/badges/BadgesPage';
import { RolesPage } from '@/pages/roles/RolesPage';
import { AccessPage } from '@/pages/access/AccessPage';
import { CouponsPage } from '@/pages/coupons/CouponsPage';
import { AnnouncementsPage } from '@/pages/announcements/AnnouncementsPage';
import { SubmissionsPage } from '@/pages/network-submissions/SubmissionsPage';
import { SubmitLocationPage } from '@/pages/network-submissions/SubmitLocationPage';
import { DeploymentPage } from '@/pages/network-submissions/DeploymentPage';
import { LeaderboardPage } from '@/pages/leaderboard/LeaderboardPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { PayoutsCompletePage } from '@/pages/settings/PayoutsCompletePage';
import { EarningRulesPage } from '@/pages/earning-rules/EarningRulesPage';
import { RfidPricingPage } from '@/pages/rfid-pricing/RfidPricingPage';
import { AutochargePage } from '@/pages/autocharge/AutochargePage';
import { McpConnectionsPage } from '@/pages/mcp-connections/McpConnectionsPage';
import { AlertSettingsPage } from '@/pages/settings/AlertSettingsPage';
import { FriendshipsPage } from '@/pages/gamification-admin/FriendshipsPage';
import { StreaksPage as AdminStreaksPage } from '@/pages/gamification-admin/StreaksPage';
import { ChallengesPage as AdminChallengesPage } from '@/pages/gamification-admin/ChallengesPage';
import { UnlocksPage as AdminUnlocksPage } from '@/pages/gamification-admin/UnlocksPage';
import { AntiAbusePage } from '@/pages/gamification-admin/AntiAbusePage';
import { PlaceholderPage } from '@/pages/placeholder/PlaceholderPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { RfidCardsPage } from '@/pages/rfid/RfidCardsPage';
import { AgentsListPage } from '@/pages/agents/AgentsListPage';
import { LeadsPage } from '@/pages/leads/LeadsPage';
import { CarbonPage } from '@/pages/carbon/CarbonPage';
import { SiteElectricityPage } from '@/pages/site-electricity/SiteElectricityPage';
import { NewsletterPage } from '@/pages/newsletter/NewsletterPage';
import { BlogPage } from '@/pages/blog/BlogPage';
import { ConversationsPage } from '@/pages/conversations/ConversationsPage';
import { IncentiveProgramsPage } from '@/pages/incentives/IncentiveProgramsPage';
import { AgentOpsQueuePage } from '@/pages/agents/AgentOpsQueuePage';
import { AgentDetailPage } from '@/pages/agents/AgentDetailPage';
import { PartnerProfilePage } from '@/pages/agents/PartnerProfilePage';
import { LockedOutDriversPage } from '@/pages/users/LockedOutDriversPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { DriversPage } from '@/pages/drivers/DriversPage';
import { GroupsPage } from '@/pages/groups/GroupsPage';
import { FleetsListPage } from '@/pages/fleets/FleetsListPage';
import { FleetDetailPage } from '@/pages/fleets/FleetDetailPage';
import { TenantsListPage } from '@/pages/tenants/TenantsListPage';
import { TenantDetailPage } from '@/pages/tenants/TenantDetailPage';

/**
 * Lightweight client-side router using browser history.
 * Replaces the minimal placeholder — real TanStack Router can come later.
 */
export function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Startup restore from the 30-day admin refresh cookie when this tab has
  // no saved session. True only while that restore runs.
  const isBootstrapping = useBootstrapAuth();
  const [path, setPath] = useState(window.location.pathname);

  // Theme is now owned end-to-end by @evi/skins' ThemeProvider
  // (mounted in main.tsx). It writes CSS variables onto :root via
  // its own effect; the legacy `useThemeStore` Zustand store that
  // wrote `<html data-theme>` here was removed along with the dead
  // `[data-theme=…]` selectors it never had. <ThemePicker /> on
  // TopNav drives mode/family changes through the same source.

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Persona reactivity ───────────────────────────────────────────
  // The persisted login-time `user` is the first-paint source (no
  // flicker — we never gate render on this). On mount / auth gain we
  // fire a LIVE /auth/me read and reconcile the persona fields the
  // nav + SP dashboard key on (role, is_sales_agent, agent_id,
  // template). This closes the staleness gap: an admin can promote a
  // user to Sales Partner (or change their role) and that user picks
  // it up on their next refresh instead of needing a re-login.
  //
  // Anti-flicker: we diff the live read against the current store and
  // only patch when something ACTUALLY changed — the common
  // no-change path is a true no-op (no store write, no re-render).
  // A reconcile re-render happens only on a genuine persona change.
  // Failure (offline, 401) silently leaves the persisted state in
  // place. Fires once per auth session; updateUser is a stable
  // Zustand action reference so it doesn't re-trigger the effect.
  const updateUser = useAuthStore((s) => s.updateUser);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        const cur = useAuthStore.getState().user;
        if (!cur) return;
        // agent_id is `string | undefined` on PublicUser (cleared =
        // undefined, not null); template is `… | null`. Normalize each
        // to its own absent-sentinel so the diff is apples-to-apples.
        const nextSalesAgent = me.is_sales_agent ?? false;
        const nextAgentId = me.agent_id ?? undefined;
        const nextTemplate = me.template ?? null;
        // The boundary flags (absent means false): a saved session picks up a
        // role change without signing out (pre-production review, 28 Oct 2026).
        const nextFacilitiesOnly = me.facilities_only === true;
        const nextSupplierOnly = me.supplier_only === true;
        // Live caps carried into the store so a later commit can filter
        // nav on actual permissions instead of role. Order-insensitive
        // compare (server sorts, but a persisted token-claim array may
        // not be) so a pure reordering doesn't trigger a needless patch.
        const nextPermissions = me.permissions ?? [];
        const curPermissions = cur.permissions ?? [];
        const permissionsEqual =
          curPermissions.length === nextPermissions.length
          && [...curPermissions].sort().join('\u0000')
            === [...nextPermissions].sort().join('\u0000');
        if (
          cur.role === me.role
          && (cur.is_sales_agent ?? false) === nextSalesAgent
          && (cur.agent_id ?? undefined) === nextAgentId
          && (cur.template ?? null) === nextTemplate
          && (cur.facilities_only === true) === nextFacilitiesOnly
          && (cur.supplier_only === true) === nextSupplierOnly
          && permissionsEqual
        ) {
          return; // no persona change — skip the patch, avoid a re-render
        }
        updateUser({
          role: me.role,
          is_sales_agent: nextSalesAgent,
          agent_id: nextAgentId,
          template: nextTemplate,
          facilities_only: nextFacilitiesOnly,
          supplier_only: nextSupplierOnly,
          permissions: nextPermissions,
        });
      })
      .catch(() => {
        // Keep persisted persona on failure — reactivity is additive,
        // never a correctness gate.
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, updateUser]);

  // Navigate programmatically without full-page reload
  const navigate = useCallback((to: string) => {
    const from = { pathname: window.location.pathname, search: window.location.search };
    window.history.pushState({}, '', to);
    setPath(window.location.pathname);
    // A move that changes only the query (the EVI shortcuts' Network
    // Chargers, /chargers?scope=network, opened from /chargers) renders
    // no new route, so the pages that keep state in their query are told:
    // the Chargers page re-reads its scope (lib/chargerScope.ts), the
    // Sessions page its ?tab=active. Never a synthetic popstate: every page
    // that follows Back would re-read its URL on a plain menu click.
    if (isQueryOnlyMove(from, window.location)) window.dispatchEvent(new Event(QUERY_CHANGE_EVENT));
  }, []);

  // Redirect authenticated users away from /login to /dashboard.
  // Catches three cases that all converge on this single effect:
  //   1. Post-login flow — onSuccess fires navigate('/dashboard'),
  //      this effect is the second-line defense if the callback
  //      ever drops back to no-op.
  //   2. Back-button after logging in — browser pops history to
  //      /login while auth state is still true.
  //   3. Direct URL entry / paste — user types localhost:5174/login
  //      while already signed in via a cookie session.
  // Without this, the authenticated branch's switch falls through
  // to the catch-all NOT FOUND for /login (no case in the switch),
  // which is exactly the bug the founder hit.
  // Phase 2 fleet platform — fleet_admin users land on their fleet
  // (the per-fleet Dashboard tab on FleetDetailPage), see fleetAdminHome
  // below. Since 2026-09-19 dashboardScope has a fleet branch and the
  // seed grants Dashboard:read, so /dashboard is ALSO theirs (session and
  // revenue widgets read the fleet's own; charger / location widgets read
  // the chargers the fleet owns and their sites) and is reachable from the nav.
  const userRole = useAuthStore((s) => s.user?.role);
  const isSupplier = useAuthStore((s) => s.user?.template === 'supplier_account' || s.user?.supplier_only === true);
  // Fleet Operator home (Warren's 2026-09-16 ruling — the per-fleet
  // stats view is the fleet_admin dashboard until the post-cutover
  // dashboardScope fix). Exactly ONE assigned fleet → /fleets/<id>,
  // straight onto that fleet's Dashboard tab. Zero or 2+ → /fleets
  // (the "no fleet assigned yet" state, or the picker).
  //
  // The count comes from the same ['fleets'] query FleetsListPage runs
  // (GET /admin/fleets, server-scoped to AccessAssignment.fleet_ids),
  // so the cache is shared and the list never double-fetches. Enabled
  // only for fleet_admin — for every other role the query never fires
  // and the home path is never read. The login redirect does NOT wait
  // on it: while unresolved the home is /fleets, and FleetsListPage
  // forwards a single-fleet operator to the detail once the list lands.
  const fleetAdminFleetsQ = useQuery({
    queryKey: ['fleets'],
    queryFn: fetchFleets,
    enabled: isAuthenticated && userRole === 'fleet_admin',
  });
  const fleetAdminSingleFleet = fleetAdminFleetsQ.data?.length === 1
    ? fleetAdminFleetsQ.data[0]
    : undefined;
  const fleetAdminHome = fleetAdminSingleFleet
    ? `/fleets/${fleetAdminSingleFleet._id}`
    : '/fleets';
  // Phase 7g.4b.1 — SP discriminator + bound Agent._id from the
  // LoginResponse. Both populated by 7g.4a + 7g.4b.1 across all
  // four login surfaces (login, magic-link, register, sales-
  // partner-invites accept). agent_id only present when
  // is_sales_agent is true; the redirect below guards on both.
  const isSalesAgent = useAuthStore((s) => !!s.user?.is_sales_agent);
  const isSalesStaff = useAuthStore((s) => ['sales_rep', 'sales_manager'].includes(s.user?.template ?? ''));
  // A Sales Partner team member (ruling 7): Jobs only, scoped by the Api to the partner's jobs.
  const isPartnerMember = useAuthStore((s) => s.user?.template === 'sales_partner_member');
  const linkedAgentId = useAuthStore((s) => s.user?.agent_id ?? null);
  // The SITE persona is all three templates, not just building_admin.
  // They share one authored menu (PERSONA_MENU in visibilityFilter.ts)
  // and one home; before this, charger_owner and property_manager were
  // in no branch at all and landed on the operator DashboardPage.
  const isSitePersona = useAuthStore((s) => (
    s.user?.template === 'building_admin'
    || s.user?.template === 'charger_owner'
    || s.user?.template === 'property_manager'
  ));
  // Building staff (Warren, 28 Oct 2026, item 5): a login a site invited from its location page. It has one page,
  // its sites' chargers, status and alerts; the Api refuses it everything else (lib/facilitiesOnlyGate.ts).
  const isBuildingStaff = useAuthStore((s) => s.user?.facilities_only === true);
  // Warren, 21 October 2026, item M: the Super Admin lands on the Today inbox.
  const isSuperAdminMenu = useAuthStore((s) => landsOnToday(s.user));
  const postLoginPath = isBuildingStaff ? '/facilities' : isSalesStaff ? '/sales-today' : isPartnerMember ? '/sales-jobs' : isSupplier ? '/supplier-pos' : userRole === 'fleet_admin'
    ? '/fleet'
    : isSuperAdminMenu ? TODAY_PATH : '/dashboard';

  // The two request forms too: the startup restore can sign a tab in while
  // it sits on one, and the signed-in switch has no page for either.
  useEffect(() => {
    if (isAuthenticated && (path === '/login' || path === '/auth/magic' || path === '/auth/forgot')) {
      navigate(takeReturnPath() ?? postLoginPath);
    }
  }, [isAuthenticated, path, navigate, postLoginPath]);

  // A load that starts signed out on a deep link (the owner notice's
  // /locations/:id/facilities, an alert email's link) renders the sign-in
  // page at that URL: remember it, so the sign-in that follows (password,
  // magic link in another tab, MFA) lands there rather than on the home
  // (lib/returnPath.ts). Never once this tab has been signed in, so signing
  // out and back in still lands on the home.
  const signedInThisLoad = useRef(false);
  useEffect(() => {
    if (isAuthenticated) { signedInThisLoad.current = true; return; }
    if (!isBootstrapping && !signedInThisLoad.current) rememberReturnPath(path);
  }, [isAuthenticated, isBootstrapping, path]);

  // Root redirect — a fleet_admin at '/' goes to their fleet home
  // (single fleet → its Dashboard tab, otherwise the list). /dashboard
  // is no longer bounced: the operator dashboard is fleet-scoped now.
  // If the fleet count resolves after this already sent them to
  // /fleets, path is no longer '/', so a later fleetAdminHome change
  // never re-navigates from here — FleetsListPage owns the forward.
  useEffect(() => {
    if (isAuthenticated && userRole === 'fleet_admin' && path === '/') {
      navigate(fleetAdminHome);
    }
  }, [isAuthenticated, userRole, path, navigate, fleetAdminHome]);

  // The logo, root URL, and sign-in all lead to the scoped Site dashboard.
  useEffect(() => {
    if (isAuthenticated && isSitePersona && path === '/') {
      navigate(isBuildingStaff ? '/facilities' : '/dashboard');
    }
  }, [isAuthenticated, isSitePersona, isBuildingStaff, path, navigate]);

  // Building staff have one page: any other path (the Site dashboard after sign-in, a typed URL) goes there. An
  // alert link (/alerts/:id, the bell's) stays, and the page opens on that alert.
  useEffect(() => {
    if (isAuthenticated && isBuildingStaff && path !== '/facilities' && path !== '/alert-preferences' && path !== '/breaker-done' && path !== '/alert-snooze' && path !== '/building-invite'
      && path !== '/site-host-onboarding' && !/^\/alerts\/[a-f0-9]{24}$/.test(path)) {
      navigate('/facilities');
    }
  }, [isAuthenticated, isBuildingStaff, path, navigate]);

  // A partner team member has Jobs and nothing else; the logo and root lead there.
  useEffect(() => {
    if (isAuthenticated && isPartnerMember && (path === '/' || path === '/dashboard')) navigate('/sales-jobs');
  }, [isAuthenticated, isPartnerMember, path, navigate]);

  // One Chargers page (Warren, 28 October 2026): the old Network Chargers
  // address opens it on the whole network, every other parameter kept
  // (lib/chargerScope.ts). replaceState, so Back never lands on the old
  // address. Signed in only: a signed-out deep link is remembered by its
  // path and redirects after sign-in. Anyone who may not choose a scope
  // lands on their own list.
  useEffect(() => {
    if (isAuthenticated && path === CHARGER_OVERSIGHT_PATH) {
      window.history.replaceState({}, '', oversightRedirectUrl(window.location.search));
      setPath('/chargers');
    }
  }, [isAuthenticated, path]);

  // Phase 7g.4b.1 — SP routing redirects.
  //   /payouts  → /reports?tab=payouts (Phase 7g.4b.3 Commit 4b
  //                                     retargeted the legacy
  //                                     spPayouts leaf in favor of
  //                                     spReports. Deep links to
  //                                     /payouts stay alive via this
  //                                     redirect so any saved
  //                                     bookmarks land on the
  //                                     equivalent surface.)
  //   /profile  → /me                  (the UserChipMenu's "Profile"
  //                                    link points everyone at
  //                                    /profile; SPs deserve /me)
  // Both fire only when the SP has an agent_id bound. If somehow
  // missing (data drift between login + render), let the SP fall
  // through to the default route handler which renders a friendly
  // "not found" rather than navigating to /agents/null. The
  // /payouts redirect drops the agentId dependency since the new
  // target doesn't need it.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isSalesAgent) {
      if (path === '/payouts') {
        // pushState writes the full URL with the query string so the
        // landed-on page sees ?tab=payouts and pre-selects the right
        // tab. setPath stores the pathname only so the route switch
        // below (which keys on '/reports') matches; the URL state is
        // the responsibility of the page's internal useReportsUrlState
        // hook from that point on. navigate() couples the two via the
        // full string, which is why this redirect splits them.
        window.history.pushState({}, '', '/reports?tab=payouts');
        setPath('/reports');
      } else if (path === '/reports' && new URLSearchParams(window.location.search).get('tab') === 'commission') {
        // The inverse of the /payouts redirect, for the tab that moved
        // OUT of Reports. Commission is no longer in the Reports tab
        // set, so an old bookmark or link to ?tab=commission would land
        // on Reports and silently fall back to the Sessions tab.
        //
        // EVERY OTHER PARAM IS CARRIED OVER. Only `tab` is dropped, and
        // only because /commissions has a single tab. A shared link is
        // usually shared for its FILTER — "the 30-day commission for
        // this site" — so dropping period/location would land the
        // reader on a different, smaller number than the sender saw and
        // give no hint that it had changed.
        const carried = new URLSearchParams(window.location.search);
        carried.delete('tab');
        const qs = carried.toString();
        window.history.replaceState({}, '', `/commissions${qs ? `?${qs}` : ''}`);
        setPath('/commissions');
      } else if (path === '/register-install') {
        // New job replaces Register Install for partners (Warren, 20 October 2026).
        navigate('/sales-jobs/new');
      } else if (path === '/profile') {
        navigate('/me');
      }
    } else {
      // Phase 7g.4c — non-SP users who land on /me (URL bar, stale
      // bookmark from when the leaf was leaking into super_admin nav,
      // anything that resolves to that path) get the inverse redirect.
      // /profile gets the super_admin profile page in a follow-up
      // commit; until then this redirect at least keeps the URL
      // surface coherent rather than dropping the user on the SP
      // page's no-agent guard.
      if (path === '/me') {
        navigate('/profile');
      }
    }
  }, [isAuthenticated, isSalesAgent, linkedAgentId, path, navigate]);

  // Phase 7g.3 — public landing page for Sales Partner invite
  // acceptance. Check this BEFORE the auth guard so both
  // authenticated and unauthenticated visitors land on it (an
  // authenticated user hitting their own already-consumed invite
  // sees the "already accepted" branch — the validate endpoint
  // gates each render). The page itself drives the auth store
  // via setAuth() on success, which flips isAuthenticated and
  // triggers the post-login navigation.
  if (path === '/sales-partner-invite') {
    return <SalesPartnerInvitePage onAccepted={(to) => navigate(to)} />;
  }
  // A Sales Partner's quote, opened by its customer from the emailed link: no sign-in, outside the Admin shell.
  const publicQuote = path.match(/^\/quote\/([A-Za-z0-9_-]{20,64})$/);
  if (publicQuote) return <CustomerQuotePage token={publicQuote[1]!} />;
  // The customer's order to pay, and a freight quote by hand (Warren, ruling A, 21 October 2026): no sign-in, the token is the key.
  const publicPay = path.match(/^\/pay\/([A-Za-z0-9_-]{20,64})$/);
  if (publicPay) return <CustomerPayPage token={publicPay[1]!} />;
  const publicFreight = path.match(/^\/freight-pay\/([A-Za-z0-9_-]{20,64})$/);
  if (publicFreight) return <FreightPayPage token={publicFreight[1]!} />;
  // A Sales Partner's portfolio, while the partner has it on: no sign-in, outside the Admin shell.
  const publicPortfolio = path.match(/^\/portfolio\/([A-Za-z0-9_-]{20,64})$/);
  if (publicPortfolio) return <PortfolioPage token={publicPortfolio[1]!} />;

  // AAP.4 — auth-token VERIFIER routes are hoisted ABOVE the auth
  // guard so they work in BOTH authenticated and unauthenticated
  // states. Same precedent as SalesPartnerInvitePage above: a token-
  // bearing link arrives by email and can be clicked at any time —
  // including while the recipient is already signed in (as a
  // DIFFERENT user, a different tenant, or themselves with a stale
  // session). Before AAP.4 these lived only in the unauthenticated
  // branch below, so an authenticated visitor fell through to the
  // NOT-FOUND fallback and couldn't accept an invite / complete a
  // reset without manually logging out first.
  //
  // No client-side signout needed on entry. On a successful verify,
  // the server endpoint clears the old one-time token, sets a fresh
  // refresh_token cookie for the NEW user, and returns the new
  // user + access token; the page's setAuth() overwrites the
  // in-memory session. The identity switch is therefore atomic on
  // success. On FAILURE (expired/invalid token) the visitor keeps
  // their existing session and sees an error — strictly better than
  // signing them out into a dead-end for clicking a stale link.
  //
  // The redirect effects above (lines 132-196) are all gated on
  // specific non-/auth paths (/login, /dashboard, /payouts, /me,
  // /profile), so none of them fire for these routes — no
  // render+redirect bounce risk.
  //
  // Note: the REQUEST forms (/auth/magic, /auth/forgot) stay in the
  // unauthenticated branch — requesting a fresh link only makes
  // sense from a signed-out state (reached via the /login page,
  // which an authenticated user is redirected away from).
  if (path === '/auth/magic-verify') {
    return <MagicLinkVerifyPage onNavigate={navigate} />;
  }
  // The building staff invite link (Warren, 28 Oct 2026, item 5): signed in or out, like the verifier above.
  if (path === '/building-invite') {
    return <BuildingInvitePage onNavigate={navigate} />;
  }
  // The "choose how often" link in every alert email (predictive maintenance stage 4): signed in or out.
  if (path === '/alert-preferences') {
    return <AlertPreferencesPage onNavigate={navigate} />;
  }
  // The Done link in the remediation ladder's building ask (step d): signed in or out; the page reads, one button records.
  if (path === '/breaker-done') {
    return <BreakerDonePage onNavigate={navigate} />;
  }
  // The snooze links in every alert email (Warren, 29 Oct 2026, item 4): signed in or out; the page reads, one button snoozes.
  if (path === '/alert-snooze') {
    return <AlertSnoozePage onNavigate={navigate} />;
  }
  // A site host's Stripe onboarding invite (29 Oct 2026, The Novelle): signed in or out; the page reads, its one button opens Stripe.
  if (path === '/site-host-onboarding') {
    return <SiteHostOnboardingPage onNavigate={navigate} />;
  }
  if (path === '/auth/reset') {
    return <ResetPasswordPage onNavigate={navigate} />;
  }
  // MFA mid-login screens (commit 4 of the admin-MFA arc) — hoisted
  // above the auth guard like the verifier routes: they sit BETWEEN
  // primary auth and the session, so neither branch owns them. Both
  // pages guard themselves on the in-memory flow state and bounce
  // to /login when it's absent (refresh, deep link, wrong variant),
  // and the enroll page completes with setAuth mid-render-tree, so
  // staying outside the guard avoids a remount mid-acknowledgment.
  if (path === '/auth/mfa/enroll') {
    return <MfaEnrollPage onNavigate={navigate} />;
  }
  if (path === '/auth/mfa/challenge') {
    return <MfaChallengePage onNavigate={navigate} />;
  }

  // After the routes above, which run their own sign-in and skip the
  // restore, and before the gate, so a valid cookie never flashes Login.
  // Only a signed-out load is ever bootstrapping.
  if (isBootstrapping) return <BootSplash />;

  if (!isAuthenticated) {
    // Path-aware unauthenticated routing. The REQUEST forms live
    // here (only reachable / meaningful from a signed-out state);
    // the token VERIFIER routes (/auth/magic-verify, /auth/reset)
    // were hoisted ABOVE the auth guard in AAP.4 so they work
    // regardless of auth state — see that block for the rationale.
    //
    //   /auth/magic  — magic-link request form (enter email →
    //                  receive sign-in link)
    //   /auth/forgot — password-reset request form (enter email →
    //                  receive reset link)
    if (path === '/auth/magic') {
      return <MagicLinkPage onNavigate={navigate} />;
    }
    if (path === '/auth/forgot') {
      return <ForgotPasswordPage onNavigate={navigate} />;
    }
    // Post-login navigation: setAuth() flips isAuthenticated to
    // true on the auth store, App re-renders past the guard above,
    // and this navigate() call moves the URL to /dashboard so the
    // authenticated switch matches the dashboard route. Without
    // this, the URL stays at /login + falls into the catch-all
    // (the redirect effect above is the safety net but the direct
    // call is the primary path).
    return (
      <LoginPage
        onNavigate={navigate}
        onSuccess={() => navigate(takeReturnPath() ?? postLoginPath)}
      />
    );
  }

  // Route matching
  if (isSupplier) return <SupplierPoPage poId={path.match(/^\/supplier-pos\/([a-f0-9]{24})$/)?.[1]} />;
  const renderPage = () => {
    // Building staff: their sites page, whatever the path (an alert link highlights that alert).
    if (isBuildingStaff) return <FacilitiesPage focusAlertId={path.match(/^\/alerts\/([a-f0-9]{24})$/)?.[1]} />;
    if (path === '/facilities') return <FacilitiesPage />;
    if (path === '/factory-settings') return <FactorySettingsPage />;
    const jobAccess = isSalesStaff || isSalesAgent || isPartnerMember || userRole === 'super_admin';
    if (path === '/sales-jobs/new') return jobAccess ? <NewJobPage onNavigate={navigate} /> : <div>Not available</div>;
    // Job-site mode: the Api decides who may see and work it.
    const siteMatch = path.match(/^\/sales-jobs\/([a-f0-9]{24})\/site$/);
    if (siteMatch) return <JobSitePage key={siteMatch[1]} jobId={siteMatch[1]!} onNavigate={navigate} />;
    const jobMatch = path.match(/^\/sales-jobs\/([a-f0-9]{24})(\/edit)?$/);
    if (jobMatch) {
      if (!jobAccess) return <div>Not available</div>;
      return jobMatch[2] ? <NewJobPage key={jobMatch[1]} jobId={jobMatch[1]} onNavigate={navigate} /> : <JobDetailPage key={jobMatch[1]} jobId={jobMatch[1]!} onNavigate={navigate} />;
    }
    // Service calls after launch (ruling H, 21 October 2026): the Api decides who sees each call and what they may do.
    if (path === '/service-requests') return <ServiceRequestsPage onNavigate={navigate} />;
    const serviceMatch = path.match(/^\/service-requests\/([a-f0-9]{24})$/);
    if (serviceMatch) return <ServiceRequestDetailPage key={serviceMatch[1]} requestId={serviceMatch[1]!} onNavigate={navigate} />;
    // A New job customer's setup before delivery (the Api answers 404 for anyone else).
    if (path === '/welcome') return <WelcomePage onNavigate={navigate} />;
    // Saved customers: the page asks the Api, which answers 404 outside a partner's job scope.
    const customerMatch = path.match(/^\/sales-customers(?:\/([a-f0-9]{24}|new))?$/);
    if (customerMatch) return <CustomersPage key={customerMatch[1] ?? 'list'} customerId={customerMatch[1]} onNavigate={navigate} />;
    // Quotes are optional (Warren, 21 October 2026); the Api decides what each viewer may do.
    // Site surveys: the Api answers 403 outside a partner's own team.
    if (path === '/sales-kit') return <SalesKitPage onNavigate={navigate} />;
    if (path === '/sales-surveys') return <SurveysPage onNavigate={navigate} />;
    const surveyMatch = path.match(/^\/sales-surveys\/([a-f0-9]{24}|new)$/);
    if (surveyMatch) return <SurveysPage key={surveyMatch[1]} surveyId={surveyMatch[1]} onNavigate={navigate} />;
    if (path === '/sales-quotes') return <QuotesPage onNavigate={navigate} />;
    const quoteMatch = path.match(/^\/sales-quotes\/([a-f0-9]{24}|new)$/);
    if (quoteMatch) return <QuoteBuilderPage key={quoteMatch[1]} quoteId={quoteMatch[1] === 'new' ? undefined : quoteMatch[1]} onNavigate={navigate} />;
    const procurementMatch = path.match(/^\/procurement\/([a-f0-9]{24})$/);
    if (procurementMatch) return <ProcurementPage key={procurementMatch[1]} poId={procurementMatch[1]} onNavigate={navigate} />;
    const deploymentMatch = path.match(/^\/deployments\/([a-f0-9]{24})$/);
    if (deploymentMatch) {
      return <DeploymentPage key={deploymentMatch[1]} deploymentId={deploymentMatch[1]} />;
    }
    // /locations/:id
    const locationDetailMatch = path.match(/^\/locations\/([a-f0-9]{24})$/);
    if (locationDetailMatch) {
      return <LocationDetailPage locationId={locationDetailMatch[1]!} onNavigate={navigate} />;
    }
    // /locations/:id/facilities: the facilities contacts link the alert
    // emails carry (Warren, 28 Oct 2026, item 6). The same page, scrolled.
    const locationFacilitiesMatch = path.match(/^\/locations\/([a-f0-9]{24})\/facilities$/);
    if (locationFacilitiesMatch) {
      return <LocationDetailPage locationId={locationFacilitiesMatch[1]!} onNavigate={navigate} focus="facilities" />;
    }

    // /chargers/:id
    const chargerDetailMatch = path.match(/^\/chargers\/([a-f0-9]{24})$/);
    if (chargerDetailMatch) {
      return <ChargerDetailPage chargerId={chargerDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /drivers/:id: the driver profile, by its own address so a session
    // row can link to it (item 9, 2026-10-23). The same DriversPage as
    // /drivers, so the list keeps its filters across a visit to a profile.
    const driverDetailMatch = path.match(/^\/drivers\/([a-f0-9]{24})$/);
    if (driverDetailMatch) {
      return <DriversPage driverId={driverDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /sessions/:id
    const sessionDetailMatch = path.match(/^\/sessions\/([a-f0-9]{24})$/);
    if (sessionDetailMatch) {
      return <SessionDetailPage sessionId={sessionDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /orders/:id (store orders — Billing-gated fulfillment surface)
    const orderDetailMatch = path.match(/^\/orders\/([a-f0-9]{24})$/);
    if (orderDetailMatch) {
      return <OrderDetailPage orderId={orderDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /fleets/:id (Phase 2 fleet platform)
    const fleetDetailMatch = path.match(/^\/fleets\/([a-f0-9]{24})$/);
    if (fleetDetailMatch) {
      return <FleetDetailPage fleetId={fleetDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /tenants/:id (super_admin tenant subscription billing detail)
    const tenantDetailMatch = path.match(/^\/tenants\/([a-f0-9]{24})$/);
    if (tenantDetailMatch) {
      return <TenantDetailPage tenantId={tenantDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /agents/:id (Phase 7e — per-agent AgentPayout history)
    // Staff get the Sales Partner profile (24 October 2026), whose Api gates
    // each part; a Sales Partner keeps its own payout history page.
    const agentDetailMatch = path.match(/^\/agents\/([a-f0-9]{24})$/);
    if (agentDetailMatch) {
      return isSalesAgent
        ? <AgentDetailPage agentId={agentDetailMatch[1]!} onNavigate={navigate} />
        : <PartnerProfilePage key={agentDetailMatch[1]} agentId={agentDetailMatch[1]!} onNavigate={navigate} />;
    }

    // /alerts/:id (the link alert notifications and emails carry,
    // lib/alertNotificationDispatch.ts linkFor): the Alerts list with
    // that alert's drawer open over it.
    const alertDetailMatch = path.match(/^\/alerts\/([a-f0-9]{24})$/);
    if (alertDetailMatch) {
      return <AlertsPage initialOpenId={alertDetailMatch[1]!} onNavigate={navigate} />;
    }

    // Today, Approvals (Warren, 29 October 2026, item 2) and one card (/approvals/:id, the link the approver email and
    // the bell carry): the Api answers each caller with the cards of the types they hold.
    const approvalsMatch = path.match(/^\/approvals(?:\/([a-f0-9]{24}))?$/);
    if (approvalsMatch) return <ApprovalsPage focusId={approvalsMatch[1] ?? null} />;

    switch (path) {
      case '/sales-payout-run': return <SalesPayoutRunPage />;
      case '/sales-team': return <SalesTeamViewPage />;
      case '/sales-today': return isSalesStaff ? <SalesTodayPage /> : <div>Not available</div>;
      // The Today inbox (Warren, 21 October 2026, item M): the Api answers each caller with only the rows they may open.
      case TODAY_PATH: return <TodayPage onNavigate={navigate} />;
      case '/sales-jobs': return isSalesStaff || isSalesAgent || isPartnerMember || userRole === 'super_admin' ? <SalesJobsPage onNavigate={navigate} /> : <div>Not available</div>;
      case '/sales-staff': return <SalesStaffPage />;
      case '/sales-statements': return <SalesStatementsPage />;
      case '/my-commission': return <SalesCommissionPage />;
      case '/sales-book/partners': return <SalesBookPartnersPage />;
      case '/sales-book/orders': return <SalesBookOrdersPage />;
      case '/sales-book/onboarding': return <SalesBookOnboardingPage />;
      case '/':
      case '/dashboard':
        if (isSalesStaff) return <DashboardPage key="sales-book" salesBook onNavigate={navigate} />;
        // Phase 7g.4b.2 — SP gets the real SalesPartnerDashboardPage
        // (commission KPIs + chart + ledger + Connect banner).
        // Visual mirror of the operator DashboardPage via shared
        // KpiCard + RevenueChart from @evi/ui. Non-SP keeps the
        // operator dashboard. App.tsx is the SP-vs-operator routing
        // gate; both pages handle their own data fetching.
        return isSalesAgent
          ? <SalesPartnerDashboardPage onNavigate={navigate} />
          : <DashboardPage onNavigate={navigate} />;

      // Phase 7g.4b.1 — SP-only placeholder routes. Real pages
      // ship in 7g.4b.3 with single-line swap-ins. Surfaced now
      // so the nav links don't 404.
      case '/my-chargers':
        // Phase 7g.4b.4 Commit 1 — real SP My Chargers page replaces
        // the PlaceholderPage stub. SP-only surface; non-SP roles
        // never reach this route because the nav leaf at nav.ts is
        // salesAgentVisible: true (no operator equivalent route).
        return <SalesPartnerMyChargersPage />;
      case '/register-install':
        // SP install-registration wizard — registers a field install
        // (customer + location + chargers) against POST /sp/installs.
        // SP-only surface, same nav gating as /my-chargers (the leaf
        // at nav.ts is visibleFor: ['sales_agent']; non-SP callers
        // hitting the URL by hand get 403s from the /sp routes and
        // the page degrades to its error state).
        return <SpRegisterInstallPage onNavigate={navigate} />;
      case '/me':
        // Phase 7g.4b.4 Commit 2 — real SP My Profile page replaces
        // the PlaceholderPage stub. SP-only surface; /profile from
        // the user-chip menu redirects to /me via the SP-conditional
        // redirect effect above. SP nav surface now fully populated:
        // Dashboard, My Chargers, Reports, My Profile.
        //
        // Phase 7g.4c Commit A added a defensive non-SP /me to
        // /profile redirect that fires before this case ever sees
        // a non-SP user. Non-SP can no longer reach the SP page.
        return <SalesPartnerProfilePage />;

      case '/profile':
        // Phase 7g.4c Commit C — operator-tier profile page for
        // non-SP users. SPs hit the /profile to /me redirect at
        // line 164 first and never reach this case. The component
        // itself does not gate by role; the redirect is the gate.
        return <SuperAdminProfilePage />;

      case '/locations':
        return <LocationsPage onNavigate={navigate} />;
      // Redirected to /chargers?scope=network by the effect above. Nothing
      // renders for the one frame before it, so ChargersPage mounts on the
      // new URL and reads the whole network scope from it.
      case CHARGER_OVERSIGHT_PATH:
        return null;
      // The dashboard Anomalies widget's 'View all' — every anomaly in
      // scope, newest onset first (Dashboard.read, same endpoint).
      case '/anomalies':
        return <AnomaliesPage onNavigate={navigate} />;
      case '/chargers':
        return <ChargersPage onNavigate={navigate} />;

      case '/chargers/missing-iccid':
        return <MissingIccidPage onNavigate={navigate} />;

      case '/alerts':
        return <AlertsPage onNavigate={navigate} />;

      case '/agent-log':
        return <AgentLogPage onNavigate={navigate} />;

      case '/sim-reconciliation':
        return <SimReconciliationPage onNavigate={navigate} />;

      case '/sessions':
        return <SessionsPage onNavigate={navigate} />;

      case '/tariffs':
      case '/tariffs/schedules':
        return <TariffsPage />;

      case '/coupons':
        return <CouponsPage />;

      case '/announcements':
        return <AnnouncementsPage />;

      case '/network-submissions':
        return <SubmissionsPage />;
      case '/submit-location':
        return <SubmitLocationPage />;
      case '/deployments':
        return <DeploymentPage key="deployments" />;

      case '/billing':
      // Money — the one leaf the framework asks for, with Revenue,
      // Payouts, Transactions and Orders as tabs. The four routes below
      // still work: nothing that deep-links to them breaks, they simply
      // have no nav leaf of their own now.
      case '/money':
        return <MoneyPage onNavigate={navigate} />;
      case '/billing/revenue':
        return <RevenuePage />;
      case '/billing/payouts':
      case '/invoices':
        return <PayoutsPage />;
      case '/transactions':
        return <SessionsPage onNavigate={navigate} />;

      // Store orders — list + detail (detail matched by regex above).
      case '/orders':
        return <OrdersPage onNavigate={navigate} />;
      case '/evi-queues':
        return userRole === 'super_admin' ? <EviQueuesPage onNavigate={navigate} /> : <div>Not available</div>;
      case '/procurement':
        return <ProcurementPage onNavigate={navigate} />;

      case '/badges':
        return <BadgesPage />;
      case '/earning-rules':
        return <EarningRulesPage />;
      case '/leaderboard':
        return <LeaderboardPage />;

      case '/roles':
        return <RolesPage onNavigate={navigate} />;
      case '/access':
        return <AccessPage onNavigate={navigate} />;

      // ── The Fleet menu's addresses ───────────────────────────
      // Each maps to FleetDetailPage with the tab the path names, on
      // the caller's OWN fleet. fleetAdminHome resolves that: exactly
      // one assigned fleet goes straight there, several fall back to
      // the picker (which is what /fleets renders).
      //
      // Stable paths rather than /fleets/<id>?tab=... because this
      // router keys on window.location.pathname — a query-only
      // navigation never re-renders.
      case '/fleet':
      case '/fleet/drivers':
      case '/fleet/keyfobs':
      case '/fleet/sessions':
      case '/fleet/money':
      case '/fleet/settings': {
        if (!fleetAdminSingleFleet) return <FleetsListPage onNavigate={navigate} />;
        const tabForPath: Record<string, 'overview' | 'drivers' | 'keyfobs' | 'sessions' | 'billing' | 'admins'> = {
          '/fleet': 'overview',
          '/fleet/drivers': 'drivers',
          '/fleet/keyfobs': 'keyfobs',
          '/fleet/sessions': 'sessions',
          '/fleet/money': 'billing',
          '/fleet/settings': 'admins',
        };
        return (
          <FleetDetailPage
            fleetId={String(fleetAdminSingleFleet._id)}
            onNavigate={navigate}
            initialTab={tabForPath[path] ?? 'overview'}
          />
        );
      }

      case '/my-site':
        return <MySitePage />;
      case '/earnings':
        return <SiteEarningsPage />;
      case '/site-settings':
        return <SiteSettingsPage />;

      case '/reports':
        // Three-way persona-conditional branch (Building Admin
        // Reports commit 7). Each persona gets its own ReportsPage
        // composing the shared ScopedReportsPage shell with its
        // tab inventory + persona-specific scope plumbing:
        //   building_admin → BuildingAdminReportsPage (2 tabs:
        //                    Booking History, Location Units
        //                    Consumed; AccessAssignment-scoped)
        //   sales_agent    → SalesPartnerReportsPage (4 tabs:
        //                    Sessions, Energy, Utilization, Payouts;
        //                    sales_agent template branch in
        //                    dashboardScope). Commission left this set
        //                    on 2026-09-24 — it is /commissions now.
        //   default        → ReportsPage (operator; 8 tabs, tenant-
        //                    wide scope)
        // building_admin checked before sales_agent because both
        // can theoretically be true on a single user (template wins
        // over is_sales_agent at the persona-resolution layer); the
        // check order mirrors the resolvePersonaId helper.
        if (isSitePersona) return <BuildingAdminReportsPage />;
        if (isSalesAgent) return <SalesPartnerReportsPage />;
        return <ReportsPage />;

      // SP Commissions — the tab promoted out of /reports. SP-only, the
      // same client-side posture as /my-chargers: the nav leaf is the
      // gate, and a hand-typed URL from a non-SP renders a page whose
      // queries 403 server-side (listAgentPayouts is super_admin-gated
      // with an SP-self bypass keyed on the caller's linked agent).
      case '/commissions':
        return <SalesPartnerCommissionsPage />;

      case '/fleets':
        return <FleetsListPage onNavigate={navigate} />;

      // Super-admin tenant roster + per-tenant subscription billing.
      // The nav leaf is super_admin-gated; the GET routes 403 others.
      case '/tenants':
        return <TenantsListPage onNavigate={navigate} />;

      case '/users':
        return <UsersPage />;
      case '/drivers':
        return <DriversPage onNavigate={navigate} />;
      case '/groups':
        return <GroupsPage />;
      case '/users/locked-out':
        return <LockedOutDriversPage />;

      case '/rfid':
        return <RfidCardsPage />;
      case '/rfid/assignments':
        return <PlaceholderPage title="RFID Assignments" subtitle="Assign cards to drivers" icon={Radio} recentLabel="Assignments" />;
      case '/settings':
        return <SettingsPage />;
      // RFID registration pricing — super_admin only. Same client-side
      // permissiveness as /agents: no route guard. The nav hides the leaf
      // for non-super_admin, and the server PUT 403s. The GET is open, so a
      // hand-typed URL renders read-only values that cannot be saved.
      case '/settings/rfid-pricing':
        return <RfidPricingPage />;
      // Autocharge — super_admin only, same client-side posture as
      // rfid-pricing: the nav hides it, the server gates writes.
      case '/settings/autocharge':
        return <AutochargePage />;
      // Connector Access: claude.ai OAuth connections to the Mcp, with Revoke.
      // Nav-gated to super_admin; GET/POST /admin/mcp/connections (also
      // super_admin only) are the security boundary.
      case '/settings/connectors':
        return <McpConnectionsPage />;
      // Settings, Alerts (Warren, 29 Oct 2026, item 5): super_admin in the nav; GET/PUT/preview /settings/alerts
      // (Super Admin only) are the boundary.
      case '/settings/alerts':
        return <AlertSettingsPage />;

      // Phase 7a — Agents admin (super_admin only).
      // The nav hides this leaf for non-super_admin via the
      // requiresSuperAdmin flag in nav.ts. Direct-URL navigation
      // for a non-super_admin still renders the page (no client-
      // side route guard), but every API call from the page hits
      // a 403 from the server's role gate — the page degrades to
      // an empty state with an error. Path of least resistance:
      // not bothering with a client guard, since the data layer
      // is the security boundary and the empty state is fine UX
      // for the rare case where someone hand-types the URL.
      case '/agents':
        return <AgentsListPage onNavigate={navigate} />;
      // Leads CRM (leads unification). Nav-gated to super_admin/operator via
      // visibleFor; the /admin/leads routes are the security boundary, so a
      // hand-typed URL for anyone else degrades to an empty error state.
      // Carbon program console (carbon epic). Nav-gated to super_admin
      // via visibleFor; the /admin/carbon-* routes are the security
      // boundary, so a hand-typed URL degrades to an error state.
      case '/carbon':
        return <CarbonPage />;
      // Site Host Terms console (super_admin). /admin/site-electricity/* and
      // the transition routes are the security boundary.
      case '/site-electricity':
        return <SiteElectricityPage />;
      case '/leads':
        return <LeadsPage onNavigate={navigate} />;
      // EVI › Partners › Applications — the "See if I qualify" review
      // queue. Its own address rather than a query string on /leads, so
      // the nav leaf is a real destination and the two do not share an
      // href. There is no separate applications model: a qualification
      // submission is a Lead captured with source 'sp_qualification'
      // (POST /public/leads), which maps to the sales_partner audience.
      case '/partner-applications':
        return (
          <LeadsPage
            onNavigate={navigate}
            initialAudience="sales_partner"
            title="Partner Applications"
          />
        );
      // Newsletter studio — compose/approve/send rich emails (super_admin/
      // operator or the campaign:send grant via visibleFor; the
      // /admin/newsletter routes are the security boundary).
      case '/newsletter':
        return <NewsletterPage />;
      // Blog studio — compose/approve/publish evinitiative.com/blog posts from
      // blocks (same back-office gate; /admin/blog routes are the boundary).
      case '/blog':
        return <BlogPage />;
      // Conversations — inbound-email agent (super_admin/operator via
      // visibleFor; the /admin/conversations routes are the boundary).
      case '/conversations':
        return <ConversationsPage onNavigate={navigate} />;
      // Incentive Programs editor (incentives v2) — curated incentive layer.
      // super_admin/operator via visibleFor; the /admin/incentives routes are
      // the security boundary.
      case '/incentives':
        return <IncentiveProgramsPage onNavigate={navigate} />;
      // Phase 7d — Agent ops attention queue. Same client-side
      // permissiveness as /agents (no route guard); the API call
      // 403s for non-super_admin and the page degrades to an
      // error state. Phase 7e — onNavigate threaded through so the
      // agent-name anchor on each row deep-links to /agents/:id
      // (per-agent history page).
      case '/agent-ops-queue':
        return <AgentOpsQueuePage onNavigate={navigate} />;
      // /settings/payouts/complete — Stripe-hosted onboarding's
      // returnUrl + refreshUrl both point here. The page reads
      // /admin/tenants/:id/connect/status and routes the operator
      // back into Settings on success, or back into Stripe on
      // unfinished requirements.
      case '/settings/payouts/complete':
        return <PayoutsCompletePage onNavigate={navigate} />;
      case '/owner-payouts':
        return <OwnerPayoutsPage />;

      // ── Gamification admin (Phase 10) ────────────────────────────
      case '/friendships':
        return <FriendshipsPage />;
      case '/streaks':
        return <AdminStreaksPage />;
      case '/challenges':
        return <AdminChallengesPage />;
      case '/unlocks':
        return <AdminUnlocksPage />;
      case '/anti-abuse':
        return <AntiAbusePage />;
      case '/audit':
        return <PlaceholderPage title="Audit Log" subtitle="System activity history" icon={FileText} recentLabel="Audit Logs" />;

      default:
        return <PlaceholderPage title="Not Found" subtitle={`No page at ${path}`} icon={Clock} />;
    }
  };

  return (
    <AppShell currentPath={path} onNavigate={navigate}>
      {/* ErrorBoundary wraps the page-content render only,
          NOT the AppShell chrome. Render throws inside a page
          component (recently caught: /access detail-view click
          blanking the root) get a visible recovery surface
          instead of an empty `<div id="root">`. The chrome
          stays interactive so the operator can navigate away
          to a working page; the boundary itself also offers a
          Reload + Back-to-dashboard CTA pair. resetKey=path
          clears the boundary's error state on route change so
          a navigated-away-from broken page doesn't keep its
          error UI persisting on a fresh page render. */}
      <ErrorBoundary resetKey={path}>
        {renderPage()}
      </ErrorBoundary>
    </AppShell>
  );
}
